import { useEffect, useMemo, useState } from "react";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { Link, useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { AppPicker } from "@/modules/shell/AppPicker";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { EmptyState } from "@/modules/shell/EmptyState";
import { stockModuleTabs } from "@/modules/shell/desk-module-tabs";
import { PageFrame } from "@/modules/shell/PageFrame";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import { listProducts } from "@/shared/api/inventory";
import { notify } from "@/shared/feedback/notify";
import {
  createConversionRecipe,
  deactivateConversionRecipe,
  listConversionRecipes,
  listConversions,
  postConversion,
  reactivateConversionRecipe,
  updateConversionRecipe,
  type ConversionListSort,
} from "@/shared/api/conversions";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { conversionHistoryColumns } from "./conversion-history-columns";

const PAGE_SIZE = 50;
const SORTABLE: ConversionListSort[] = ["number", "createdAt"];

function sortingToApi(sorting: SortingState): {
  sort: ConversionListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as ConversionListSort)) {
    return {
      sort: first.id as ConversionListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

const recipeCreateSchema = z.object({
  fromProductId: z.string().min(1, "From product required"),
  toProductId: z.string().min(1, "To product required"),
  factor: z.string().trim().min(1, "Factor required"),
  recipeName: z.string().trim().optional(),
});
type RecipeCreateInput = z.input<typeof recipeCreateSchema>;

export function ConvertPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const [recipeId, setRecipeId] = useState("");
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null);
  const [editRecipeName, setEditRecipeName] = useState("");
  const [qtyFrom, setQtyFrom] = useState("1");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [fromLotCode, setFromLotCode] = useState("");
  const [fromSerialCode, setFromSerialCode] = useState("");
  const [toLotCode, setToLotCode] = useState("");
  const [toSerialCode, setToSerialCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const { searchParams, patchParams } = useListUrlParams();
  const [historyQueryText, setHistoryQueryText] = useState(() =>
    readQ(searchParams),
  );
  const [debouncedHistoryQ, setDebouncedHistoryQ] = useState(() =>
    readQ(searchParams),
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = historyQueryText.trim();
      setDebouncedHistoryQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [historyQueryText, patchParams]);

  const productsQuery = useQuery({
    queryKey: queryKeys.products.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listProducts(companySlug!),
  });
  const recipesQuery = useQuery({
    queryKey: queryKeys.conversions.recipes(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listConversionRecipes(companySlug!, { includeDeactivated: true }),
  });
  const historyQuery = useInfiniteQuery({
    queryKey: queryKeys.conversions.list(companySlug ?? "", {
      q: debouncedHistoryQ,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listConversions(companySlug!, {
        q: debouncedHistoryQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const errorMsg = queryErrorMessage(
    productsQuery.error ?? recipesQuery.error ?? historyQuery.error,
    productsQuery.isError || recipesQuery.isError || historyQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const products = (productsQuery.data?.products ?? []).filter(
    (x) => x.kind === "stocked",
  );
  const recipes = recipesQuery.data?.recipes ?? [];
  const history =
    historyQuery.data?.pages.flatMap((p) => p.conversions) ?? [];
  const historyTotal = historyQuery.data?.pages[0]?.total;
  const historyLoading = historyQuery.isPending;
  const activeRecipes = recipes.filter((r) => r.deactivatedAt == null);
  const historyColumns = useMemo(
    () => (companySlug ? conversionHistoryColumns(companySlug) : []),
    [companySlug],
  );
  const selectedRecipe = activeRecipes.find((r) => r.id === recipeId);
  const fromProduct = products.find(
    (p) => p.id === selectedRecipe?.fromProductId,
  );
  const toProduct = products.find((p) => p.id === selectedRecipe?.toProductId);
  const needFromLot = Boolean(fromProduct?.trackLot);
  const needFromSerial = Boolean(fromProduct?.trackSerial);
  const needToLot = Boolean(toProduct?.trackLot);
  const needToSerial = Boolean(toProduct?.trackSerial);

  useEffect(() => {
    if (recipeId && !activeRecipes.some((x) => x.id === recipeId)) {
      setRecipeId(activeRecipes[0]?.id ?? "");
    } else if (!recipeId && activeRecipes[0]) {
      setRecipeId(activeRecipes[0].id);
    }
  }, [activeRecipes, recipeId]);

  const recipeForm = useForm<RecipeCreateInput>({
    resolver: zodResolver(recipeCreateSchema),
    defaultValues: {
      fromProductId: "",
      toProductId: "",
      factor: "12",
      recipeName: "",
    },
  });

  useEffect(() => {
    if (!products.length) return;
    const cur = recipeForm.getValues();
    if (!cur.fromProductId && products[0]) {
      recipeForm.setValue("fromProductId", products[0].id);
    }
    if (!cur.toProductId && products[1]) {
      recipeForm.setValue("toProductId", products[1].id);
    } else if (!cur.toProductId && products[0]) {
      recipeForm.setValue("toProductId", products[0].id);
    }
  }, [products, recipeForm]);

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.conversions.all(companySlug),
    });
  }

  const createRecipe = useMutation({
    mutationFn: async (raw: RecipeCreateInput) => {
      const body = recipeCreateSchema.parse(raw);
      return createConversionRecipe(companySlug!, {
        fromProductId: body.fromProductId,
        toProductId: body.toProductId,
        factor: body.factor,
        name: body.recipeName || undefined,
      });
    },
    onSuccess: async (res) => {
      recipeForm.setValue("recipeName", "");
      setError(null);
      setStatus(
        res.recipe.reactivated
          ? "Recipe reactivated with new factor"
          : "Recipe saved",
      );
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed");
    },
  });

  const filtersActive = Boolean(historyQueryText.trim());
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setHistoryQueryText,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query: historyQueryText,
    debouncedQ: debouncedHistoryQ,
    isFetching: historyQuery.isFetching,
  });

  if (!companySlug) return null;

  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function onConvert(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    /** One key for soft-confirm retries so a flaky retry cannot double-convert — plan/24 */
    const idempotencyKey = `cv-${crypto.randomUUID()}`;
    const payload = {
      recipeId: recipeId || undefined,
      qtyFrom,
      idempotencyKey,
      ...(warehouseId ? { warehouseId } : {}),
      ...(fromLotCode.trim() ? { fromLotCode: fromLotCode.trim() } : {}),
      ...(fromSerialCode.trim()
        ? { fromSerialCode: fromSerialCode.trim() }
        : {}),
      ...(toLotCode.trim() || fromLotCode.trim()
        ? { toLotCode: toLotCode.trim() || fromLotCode.trim() }
        : {}),
      ...(toSerialCode.trim()
        ? { toSerialCode: toSerialCode.trim() }
        : {}),
    };
    try {
      const result = await postConversion(companySlug!, payload);
      setFromLotCode("");
      setFromSerialCode("");
      setToLotCode("");
      setToSerialCode("");
      if (result.conversion.oversell) {
        notify.info("Converted — source below recorded on-hand (audited)");
      } else {
        notify.success("Conversion posted");
      }
      navigate(
        `/c/${companySlug}/inventory/conversions/${result.conversion.id}`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }


  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="Convert / unpack"
        description="e.g. 1 box → 12 pcs · value preserved on moving average. Deactivate recipes you no longer use."
        tabs={
          <DeskSubNav
            placement="header"
            items={stockModuleTabs(companySlug)}
            aria-label="Stock views"
          />
        }
      />

      <form className="mt-8 space-y-3" onSubmit={(e) => void onConvert(e)}>
        <label className="block text-sm">
          <span className="text-[var(--color-muted)]">Recipe</span>
          <AppPicker
            className="mt-1"
            title="Recipe"
            description="Search conversion recipes"
            placeholder="Select…"
            value={recipeId || null}
            options={activeRecipes.map((r) => ({
              value: r.id,
              label: r.name || `${r.fromName} → ${r.toName}`,
              description: `×${r.factor}`,
              keywords: `${r.name ?? ""} ${r.fromName} ${r.toName}`,
            }))}
            emptyLabel="No active recipes"
            onChange={(id) => setRecipeId(id ?? "")}
          />
        </label>
        <label className="block text-sm">
          <span className="text-[var(--color-muted)]">Qty of from-unit</span>
          <Input
            className="mt-1 font-mono"
            value={qtyFrom}
            onChange={(e) => setQtyFrom(e.target.value)}
            required
          />
        </label>
        {(needFromLot || needFromSerial || needToLot || needToSerial) && (
          <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/70 p-4">
            <p className="m-0 text-xs text-[var(--color-muted)]">
              Lot / serial (required when product tracks them)
            </p>
            {needFromLot && (
              <label className="block text-sm">
                <span className="text-[var(--color-muted)]">From lot</span>
                <Input
                  className="mt-1 font-mono"
                  value={fromLotCode}
                  onChange={(e) => setFromLotCode(e.target.value)}
                  required
                  placeholder="LOT-…"
                />
              </label>
            )}
            {needFromSerial && (
              <label className="block text-sm">
                <span className="text-[var(--color-muted)]">From serial</span>
                <Input
                  className="mt-1 font-mono"
                  value={fromSerialCode}
                  onChange={(e) => setFromSerialCode(e.target.value)}
                  required
                  placeholder="SN-…"
                />
              </label>
            )}
            {needToLot && (
              <label className="block text-sm">
                <span className="text-[var(--color-muted)]">
                  To lot{needFromLot ? " (defaults to from)" : ""}
                </span>
                <Input
                  className="mt-1 font-mono"
                  value={toLotCode}
                  onChange={(e) => setToLotCode(e.target.value)}
                  required={!needFromLot}
                  placeholder={needFromLot ? "Same as from if blank" : "LOT-…"}
                />
              </label>
            )}
            {needToSerial && (
              <label className="block text-sm">
                <span className="text-[var(--color-muted)]">To serial</span>
                <Input
                  className="mt-1 font-mono"
                  value={toSerialCode}
                  onChange={(e) => setToSerialCode(e.target.value)}
                  required
                  placeholder="SN-…"
                />
              </label>
            )}
          </div>
        )}
        <WarehouseSelect
          companySlug={companySlug}
          value={warehouseId}
          onChange={setWarehouseId}
          disabled={busy}
        />
        <Button type="submit" disabled={busy || !recipeId}>
          {busy ? "Posting…" : "Convert"}
        </Button>
      </form>

      {recipes.length > 0 && (
        <div className="mt-10 border-t border-[var(--color-border)] pt-6">
          <h2 className="m-0 text-sm font-semibold">Recipes</h2>
          <AdminList>
            {recipes.map((r) => {
              const deactivated = r.deactivatedAt != null;
              const label = r.name || `${r.fromName} → ${r.toName}`;
              return (
                <AdminListRow
                  key={r.id}
                  className="flex items-center justify-between gap-2 px-4 py-3.5 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    {editingRecipeId === r.id ? (
                      <Input
                        className="max-w-xs"
                        value={editRecipeName}
                        onChange={(e) => setEditRecipeName(e.target.value)}
                        placeholder={`${r.fromName} → ${r.toName}`}
                        disabled={busy}
                        autoFocus
                      />
                    ) : (
                      <>
                        {label}
                        <span className="font-mono text-xs text-[var(--color-muted)]">
                          {" "}
                          · ×{r.factor}
                        </span>
                        {deactivated ? (
                          <span className="text-xs text-[var(--color-muted)]">
                            {" "}
                            · deactivated
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                  {editingRecipeId === r.id ? (
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="sm"
                        pending={busy}
                        pendingLabel="Saving…"
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            setError(null);
                            try {
                              const name = editRecipeName.trim() || null;
                              await updateConversionRecipe(companySlug, r.id, {
                                name,
                              });
                              setStatus(
                                name
                                  ? `Renamed to ${name}`
                                  : "Recipe name cleared",
                              );
                              setEditingRecipeId(null);
                              await reload();
                            } catch (err: unknown) {
                              setError(
                                err instanceof Error ? err.message : "Failed",
                              );
                            } finally {
                              setBusy(false);
                            }
                          })();
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => setEditingRecipeId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : !deactivated ? (
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => {
                          setEditingRecipeId(r.id);
                          setEditRecipeName(r.name ?? "");
                          setError(null);
                          setStatus(null);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirmUi({
                            title: `Deactivate “${label}”?`,
                            description: "Hidden from new converts.",
                            confirmLabel: "Deactivate",
                            destructive: true,
                          });
                          if (!ok) return;
                          setBusy(true);
                          setError(null);
                          try {
                            await deactivateConversionRecipe(companySlug, r.id);
                            setStatus(`${label} deactivated`);
                            await reload();
                          } catch (err: unknown) {
                            setError(
                              err instanceof Error ? err.message : "Failed",
                            );
                          } finally {
                            setBusy(false);
                          }
                        })();
                      }}
                    >
                      Deactivate
                    </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        setBusy(true);
                        setError(null);
                        void reactivateConversionRecipe(companySlug, r.id)
                          .then(async () => {
                            setStatus(`${label} reactivated`);
                            await reload();
                          })
                          .catch((err: unknown) =>
                            setError(
                              err instanceof Error ? err.message : "Failed",
                            ),
                          )
                          .finally(() => setBusy(false));
                      }}
                    >
                      Reactivate
                    </Button>
                  )}
                </AdminListRow>
              );
            })}
          </AdminList>
        </div>
      )}

      <form
        className="mt-10 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={recipeForm.handleSubmit((values) =>
          createRecipe.mutate(values),
        )}
      >
        <h2 className="m-0 text-sm font-semibold">Add recipe</h2>
        <label className="block text-sm">
          From
          <Controller
            control={recipeForm.control}
            name="fromProductId"
            render={({ field }) => (
              <AppPicker
                className="mt-1"
                title="From product"
                description="Search stocked products"
                placeholder="From product…"
                value={field.value || null}
                options={products.map((p) => ({
                  value: p.id,
                  label: p.name,
                  description: p.sku ?? undefined,
                  keywords: `${p.name} ${p.sku ?? ""} ${p.barcode ?? ""}`,
                }))}
                emptyLabel="No stocked products"
                onChange={(id) => field.onChange(id ?? "")}
              />
            )}
          />
        </label>
        <label className="block text-sm">
          To
          <Controller
            control={recipeForm.control}
            name="toProductId"
            render={({ field }) => (
              <AppPicker
                className="mt-1"
                title="To product"
                description="Search stocked products"
                placeholder="To product…"
                value={field.value || null}
                options={products.map((p) => ({
                  value: p.id,
                  label: p.name,
                  description: p.sku ?? undefined,
                  keywords: `${p.name} ${p.sku ?? ""} ${p.barcode ?? ""}`,
                }))}
                emptyLabel="No stocked products"
                onChange={(id) => field.onChange(id ?? "")}
              />
            )}
          />
        </label>
        <label className="block text-sm">
          Factor (to per 1 from)
          <Input
            className="mt-1 font-mono"
            {...recipeForm.register("factor")}
          />
        </label>
        <label className="block text-sm">
          Name (optional)
          <Input
            className="mt-1"
            placeholder="Unpack box"
            {...recipeForm.register("recipeName")}
          />
        </label>
        <Button
          type="submit"
          variant="outline"
          disabled={busy || createRecipe.isPending}
        >
          Save recipe
        </Button>
      </form>

      {status && (
        <p className="mt-4 text-sm text-[var(--color-muted)]">{status}</p>
      )}
      {displayError && (
        <Alert variant="destructive" className="mt-2">
          {displayError}
        </Alert>
      )}

      <section className="mt-10 border-t border-[var(--color-border)] pt-6">
        <h2 className="m-0 text-sm font-semibold">History</h2>
        <AdminFilters>
          <Input
            className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
            placeholder="Search conversion number…"
            value={historyQueryText}
            onChange={(e) => setHistoryQueryText(e.target.value)}
          />
        </AdminFilters>
        <ListSearchStatus online searching={searchingOnline} />
        {history.length > 0 && historyTotal != null ? (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Showing {history.length}
            {historyTotal > history.length ? ` of ${historyTotal}` : ""}
          </p>
        ) : null}
        {historyLoading && (
          <Alert variant="muted" className="mt-4">
            Loading conversions…
          </Alert>
        )}
        {!historyLoading && history.length === 0 && !displayError ? (
          <div className="mt-4">
            <EmptyState
              title={
                debouncedHistoryQ
                  ? `No match for “${debouncedHistoryQ}”`
                  : "No conversions yet"
              }
              description={
                debouncedHistoryQ
                  ? "Try another conversion number."
                  : "Posted converts show up here."
              }
            />
          </div>
        ) : null}
        {history.length > 0 ? (
          <>
            <DataTable
              onRefresh={() => void historyQuery.refetch()}
              listId="conversion-history"
              companySlug={companySlug}
              columns={historyColumns}
              data={history}
              getRowId={(r) => r.id}
              sorting={sorting}
              onSortingChange={setSorting}
            />
            {historyQuery.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  disabled={historyQuery.isFetchingNextPage}
                  pending={historyQuery.isFetchingNextPage}
                  pendingLabel="Loading more…"
                  onClick={() => void historyQuery.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      <p className="mt-6">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}/products`} className="no-underline">
            ← Products
          </Link>
        </Button>
      </p>
          </DataTableChrome>
    </PageFrame>
  );
}
